import React, { useEffect, useState } from "react";
import { ButtonGroup, ToggleButton } from "react-bootstrap";
import ReferenceForm from "../../components/questionnaires/ReferenceForm";
import CandidateForm from "../../components/questionnaires/CandidateForm";
import { Client } from "../../interfaces/client";
import { useAuth } from "../../auth";
import { checkForms, getClientByEmail } from "../../services/clientService";
import NotificationsPopup from "../../components/client-components/NotificationsPopup";
import { ClientNotification } from "../../interfaces/clientNotification";
import { getNotifications } from "../../services/notificationsService";

const Questionnaires = () => {
  const [selectedOption, setSelectedOption] = useState("Candidate");
  const [showAbbreviation, setShowAbbreviation] = useState(false);
  const { decodeToken } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const loadClientAndQuestions = async () => {
      try {
        const email = decodeToken(localStorage.getItem("token") || "");
        const clientIDString = localStorage.getItem("clientID");
        const clientID = clientIDString ? parseInt(clientIDString, 10) : 0;
        const client = await getClientByEmail(email, clientID);
        setClient(client);

        const [showAbbreviationResponse] = await checkForms(client.clientID);
        setShowAbbreviation(showAbbreviationResponse);

        const notificationsResponse = await getNotifications(client?.clientID);
        notificationsResponse.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        setNotifications(notificationsResponse);

        const unreadCount = notificationsResponse.length;
        setUnreadCount(unreadCount);
      } catch (error) {
        console.error("Error loading data:", error);
        setErrorMessage("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadClientAndQuestions();
  }, [decodeToken]);

  return (
    <>
      <div>
        <div className="content-container">
          <div className="header-container">
            <h1>Questionnaires</h1>
            <div className="page-items-container">
              <div className="field-long">
                <label className="field-label">Client Name:</label>
                <div>
                  <input
                    className="form-control"
                    type="text"
                    value={client?.clientName || ""}
                    disabled
                    readOnly
                  />
                </div>
              </div>
              <div className="field-long" style={{ flex: "0 0 40%" }}>
                <label className="field-label">
                  Abbreviation/Client Number:
                </label>
                <div>
                  <input
                    className="form-control"
                    type="text"
                    value={
                      showAbbreviation
                        ? client?.abbreviation || ""
                        : "not available."
                    }
                    disabled
                    readOnly
                  />
                </div>
              </div>
              <button
                className="notification-button"
                onClick={() => setShowNotifications(true)}
              >
                <i className="bi bi-bell-fill"></i>
                {unreadCount > 0 && (
                  <span className="notification-dot">{unreadCount}</span>
                )}
              </button>
            </div>
            {showNotifications && client?.clientID && (
              <NotificationsPopup
                notificationsInput={notifications}
                clientID={client?.clientID}
                onClose={(
                  newUnreadCount = unreadCount,
                  newNotifications = notifications,
                ) => {
                  setNotifications(newNotifications);
                  setShowNotifications(false);
                  setUnreadCount(newUnreadCount);
                }}
              />
            )}
          </div>

          {loading ? (
            <div className="loading-divider">
              <div className="loading-bar"></div>
            </div>
          ) : (
            <>
              <div className="divider"></div>
              {!showAbbreviation && (
                <div className="text-container error">
                  Please complete necessary forms in order to start ordering
                  background screening and referencing. Then your abbreviation
                  will become visible.{" "}
                </div>
              )}
              <ButtonGroup className="w-100">
                <ToggleButton
                  id="candidate-option"
                  type="radio"
                  variant={
                    selectedOption === "Candidate"
                      ? "primary"
                      : "outline-primary"
                  }
                  name="option"
                  value="Candidate"
                  checked={selectedOption === "Candidate"}
                  onChange={(e) => setSelectedOption(e.currentTarget.value)}
                  className={`w-50 ${
                    selectedOption === "Candidate"
                      ? "toggle-active"
                      : "toggle-outline"
                  }`}
                >
                  Candidate Questionnaire
                </ToggleButton>
                <ToggleButton
                  id="reference-option"
                  type="radio"
                  variant={
                    selectedOption === "Reference"
                      ? "primary"
                      : "outline-primary"
                  }
                  name="option"
                  value="Reference"
                  checked={selectedOption === "Reference"}
                  onChange={(e) => setSelectedOption(e.currentTarget.value)}
                  className={`w-50 ${
                    selectedOption === "Reference"
                      ? "toggle-active"
                      : "toggle-outline"
                  }`}
                >
                  Reference Questionnaire
                </ToggleButton>
              </ButtonGroup>
              <div className="mt-3">
                {client?.questionnairesReviewed ? (
                  <div>
                    {selectedOption === "Reference" && (
                      <ReferenceForm isEditing={false} />
                    )}
                    {selectedOption === "Candidate" && (
                      <CandidateForm
                        clientID={client?.clientID ? client?.clientID : 0}
                        orderFormID={0}
                        isReadOnly={true}
                        isEditing={false}
                        fiveYear={false}
                      />
                    )}
                  </div>
                ) : (
                  <div className="text-body" style={{ textAlign: "center" }}>
                    No Questionnaires are available yet. This may be because
                    your position code(s) do not include reference screening, or
                    Oxford Document Management is still constructing your
                    personalized questionnaires.
                  </div>
                )}
              </div>
            </>
          )}
          {errorMessage && (
            <div
              className="alert-message alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Questionnaires;
